import {
  Box,
  Checkbox,
  FieldLabel,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import noop from 'lodash/noop';
import { useState } from 'react';

import { createAppModal } from 'utils/modal';

import styles from './confirmModal.module.scss';

/** A component that displays a confirm modal. */
export const ConfirmModal = createAppModal(
  { type: 'app:confirm' },
  ({ isOpen, modal, meta, hide = noop }): JSX.Element => {
    const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [confirm, setConfirm] = useState<() => Promise<void>>();

    /** Handles executing the confirm action. */
    const handleExecuteConfirm = (confirmFn?: () => Promise<void>) => {
      const action = confirmFn ?? confirm;

      if (action) {
        action()
          .then(hide)
          .catch(() => {
            setIsLoading(false);
          });
      }
    };

    /** Handles confirming the modal. */
    const handleConfirm = (confirmFn: () => Promise<void>) => {
      setConfirm(() => confirmFn);
      handleExecuteConfirm(confirmFn);
    };

    /** Handles confirming the modal. */
    const handleModalConfirm = () => {
      if (meta) {
        if (modal.arg.confirm?.async) {
          setIsLoading(true);
        } else {
          handleHide();
        }

        if (confirm) {
          handleExecuteConfirm();
        } else {
          meta.resolve({
            isConfirmed: true,
            isCancelled: false,
            hide: handleHide,
            confirm: handleConfirm,
            isCheckboxChecked,
          });
        }
      }
    };

    /** Handles hiding the modal. */
    const handleHide = () => {
      hide?.();
    };

    /** Handles cancelling the modal. */
    const handleModalCancel = () => {
      if (meta) {
        handleHide();

        meta.resolve({
          isConfirmed: false,
          isCancelled: true,
          hide: handleHide,
          confirm: handleConfirm,
          isCheckboxChecked,
        });
      }
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleModalCancel}
        className={styles.root}
      >
        <ModalHead title={modal.arg.title} onClose={handleModalCancel} />

        <ModalBody>{modal.arg.description}</ModalBody>

        <ModalFoot
          confirmOnClick={handleModalConfirm}
          cancelOnClick={modal.arg.cancel?.hide ? undefined : handleModalCancel}
          cancelName={modal.arg.cancel?.label ?? 'Cancel'}
          confirmName={modal.arg.confirm?.label ?? 'Confirm'}
          confirmProps={{
            variant: modal.arg.confirm?.variant ?? 'danger',
            loadingIndicator: modal.arg.confirm?.asyncLabel,
            isLoading,
          }}
          cancelProps={{
            disabled: isLoading,
          }}
        >
          {modal.arg.checkbox?.label && (
            <FlexBox flex="1" alignItems="center" justifyContent="start">
              <FieldLabel>
                <Checkbox
                  name="confirmModalCheckbox"
                  checked={isCheckboxChecked}
                  onChange={() =>
                    setIsCheckboxChecked(
                      (isCheckboxChecked) => !isCheckboxChecked,
                    )
                  }
                />
                <Box as="span" margin="100">
                  {modal.arg.checkbox.label}
                </Box>
              </FieldLabel>
            </FlexBox>
          )}
        </ModalFoot>
      </Modal>
    );
  },
);
